import { Component, PLATFORM_ID, inject } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { Router, RouterModule } from '@angular/router';
import { ClinicService } from '../../services/clinic.service';
import { UserService } from '../../services/user.service';
import { Clinic } from '../../interfaces/clinic.interface';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  catchError,
  debounceTime,
  forkJoin,
  map,
  of,
  switchMap,
  takeWhile,
  tap,
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { IconWrapComponent } from '../../components/icon-wrap-component/icon-component';
import { SearchService } from '../../services/search.service';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { environment } from '../../environments/environment.prod';
import { Pet } from '../../interfaces/pet.interface';
import { ClientService } from '../../services/client.service';
import { FlowbiteService } from '../../services/flowbite.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    IconWrapComponent,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  router = inject(Router);
  authSrv = inject(AuthService);
  clinicSrv = inject(ClinicService);
  searchSrv = inject(SearchService);
  clientSrv = inject(ClientService);
  userSrv = inject(UserService);
  flowbiteSrv = inject(FlowbiteService);
  clinics: Clinic[] = [];
  toastr = inject(ToastrService);
  canAddClinic = false;
  powerfulRoles = ['administrator', 'owner'];
  activeClinic: Clinic | null = null;
  user$ = this.userSrv.user$.pipe(
    takeWhile(() => this.isBrowser),
    tap((user) => {
      if (!user) return;
      this.canAddClinic = this.powerfulRoles.includes(user.role);
      if (user.availableClinics.length) {
        this.getActiveClinics(user.availableClinics);
      }
    })
  );
  searchPlaceholders = {
    patients: 'Търсете по име или порода',
    clients: 'Търсете по име, тел. номер или Email',
    stocks: 'Търсете по име продукта или услугата',
  };
  assetUrl = environment.asset_url;
  type: 'patients' | 'clients' | 'stocks' = 'patients';
  search = new FormControl('', { nonNullable: true });
  search$ = this.search.valueChanges.pipe(
    debounceTime(300),
    switchMap((searchQuery) =>
      this.searchSrv.searchByQuery(this.type, searchQuery).pipe(
        catchError(() => of([])),
        switchMap((response) => {
          if (this.type === 'patients') {
            const data = response as Pet[];
            return forkJoin(
              data.map((pet) =>
                this.clientSrv
                  .detailsUser(pet.ownerId as string)
                  .pipe(map((owner) => ({ ...pet, owner })))
              )
            );
          }
          return of(response);
        })
      )
    )
  );

  constructor() {
    if (!this.isBrowser) return;
  }
  setType(value: 'patients' | 'clients' | 'stocks') {
    this.type = value;
    this.search.patchValue('');
  }
  logout() {
    this.router.navigate(['/auth', 'login'], {
      replaceUrl: true,
    });
    this.authSrv.logout();
  }
  getRedirectUrl(item: any) {
    if (this.type === 'clients') {
      return {
        url: `/admin-panel/pet-owners`,
        queryParams: {
          targetId: item._id,
        },
      };
    }
    if (this.type === 'patients') {
      return {
        url: `/admin-panel/patients/` + item._id + '/' + item.owner._id,
        queryParams: {
          // targetId: item._id,
        },
      };
    }
    if (this.type === 'stocks') {
      const stockTypes = {
        service: `/admin-panel/stocks/${item.clinicId}/services`,
        product: `/admin-panel/stocks/${item.clinicId}/products`,
        foodSupplement: `/admin-panel/stocks/${item.clinicId}/food-supplements`,
        accessory: `/admin-panel/stocks/${item.clinicId}/accessories`,
      };
      return {
        url: stockTypes[item['type'] as keyof typeof stockTypes],
        queryParams: {
          targetId: item._id,
        },
      };
    } else
      return {
        url: 'UNDEFINED',
        queryParams: {},
      };
  }
  getActiveClinics(clinicIds: string[]) {
    forkJoin(clinicIds.map((id) => this.clinicSrv.getDetails(id))).subscribe({
      next: (response) => {
        this.clinics = response;
        const activeClinic = this.clinics.find(
          (x) => x._id === this.userSrv.user$.value.activeClinic
        );
        if (activeClinic) {
          this.activeClinic = activeClinic;
        }
      },
    });
  }
  setActiveClinic(index: number) {
    const clinic = this.clinics[index];
    this.userSrv
      .editUser(this.userSrv.user$.value._id, {
        activeClinic: clinic._id,
      })
      .subscribe({
        next: () => {
          this.activeClinic = clinic;
          this.toastr.success('Избрана е активна клиника.');
        },
      });
  }
}
